import { bbox, centroid, Geometry, getCoords, midpoint, point } from '@turf/turf';

import { AnomalyMapFilters, GeoJSONGeometry, LegacyAnomaly } from 'components/inspections/types';
import { AnomalyDefinitionMap, MapAnomalies } from 'components/Sites/types';
import {
    AssetTypes,
    DIFFERENTIAL_MODES,
    DISPLAY_MAP_LAYER_KEYS,
    EMPTY_SIGNAL_OR_MODIFIER,
    LOCAL_STORAGE_KEYS,
    MAP_LAYER_ASSET_TYPES,
} from 'lib/constants';
import { AssetNomenclature } from 'lib/hooks/map/useAssetNomenclature';

import { buildAOIObservations } from './inspections';
import { getAsJSON } from './localStorage';

export const getSavedFilters = (
    defaultFilteredAssetLayers: DISPLAY_MAP_LAYER_KEYS[],
    filterKey: string,
): AnomalyMapFilters => {
    const savedFilters = getAsJSON(LOCAL_STORAGE_KEYS.ANOMALY_MAP_FILTERS) ?? {};
    const appliedFilters = filterKey ? (savedFilters[filterKey] ?? {}) : {};

    return {
        rgb: [],
        ir: [],
        observationIds: [],
        statusIds: [],
        remediationFilters: [],
        clientAttachmentFilters: [],
        layerFilters: defaultFilteredAssetLayers,
        ...appliedFilters,
    };
};

export const getSavedLayerFilters = (
    defaultFilteredAssetLayers: DISPLAY_MAP_LAYER_KEYS[],
    filterKey: string,
): AnomalyMapFilters => {
    const savedFilters = getSavedFilters(defaultFilteredAssetLayers, filterKey);

    return {
        rgb: [],
        ir: [],
        observationIds: [],
        statusIds: [],
        remediationFilters: [],
        layerFilters: savedFilters.layerFilters,
        clientAttachmentFilters: [],
    };
};

export const generateLayerFilter =
    (minimapViewId: string, minimapLayers: string[]) =>
    ({ layer, viewport }: any) => {
        const shouldDrawInMinimap = minimapLayers.filter((layerName) => layer.id.startsWith(layerName)).length > 0;

        if (viewport.id === minimapViewId) {
            return shouldDrawInMinimap;
        } else {
            return !shouldDrawInMinimap;
        }
    };

export const getCenterPointFromGeometry = (geometry: GeoJSONGeometry): number[] => {
    if (['Polygon', 'MultiPolygon'].includes(geometry.type)) {
        const coords: number[] = getCoords(centroid(geometry));

        return coords;
    } else {
        return [0, 0];
    }
};

export const getRightBorderMidpoint = (geometry: GeoJSON.Geometry): number[] => {
    const feature = { type: 'Feature', properties: {}, geometry };

    const [, minLat, maxLng, maxLat] = bbox(feature);

    const topRight = point([maxLng, maxLat]);
    const bottomRight = point([maxLng, minLat]);

    const rightEdgeMidpoint = midpoint(topRight, bottomRight);

    return rightEdgeMidpoint.geometry.coordinates;
};

// Deckgl helpers
export const getBBoxFromGeometry = (geometry: Geometry) => {
    const boundingBox = bbox(geometry);
    const formatBounds = [
        [boundingBox[0], boundingBox[1]],
        [boundingBox[2], boundingBox[3]],
    ];

    return formatBounds;
};

export const getHandleViewStateChange =
    (setViewState: any, minimapHeight = 0.2, minimapWidth = 0.2) =>
    ({ viewState }: any) => {
        setViewState((prev: any) => ({
            main: viewState,
            minimap: {
                ...prev.minimap,
                longitude: viewState.longitude,
                latitude: viewState.latitude,
                // Since we don't directly manipulate the minimap viewstate,
                // we need to manage the height and width manually
                height: viewState.height * minimapHeight,
                width: viewState.width * minimapWidth,
            },
        }));
    };

export const getImageUrlsFromLegacyAnomaly = (
    legacyAnomaly: LegacyAnomaly,
    showSampleImages: boolean,
): { irUrl: string | undefined; vizUrl: string | undefined; isIrSample: boolean; isVizSample: boolean } => {
    let irUrl = legacyAnomaly?.images.find((image) => image.imageType === 'ir')?.url;
    let vizUrl = legacyAnomaly?.images.find((image) => image.imageType === 'viz')?.url;
    let isIrSample = false;
    let isVizSample = false;

    if (!irUrl && legacyAnomaly?.irSampleImages.length && showSampleImages) {
        irUrl = legacyAnomaly?.irSampleImages[0].url;
        isIrSample = true;
    }

    if (!vizUrl && legacyAnomaly?.vizSampleImages.length && showSampleImages) {
        vizUrl = legacyAnomaly?.vizSampleImages[0].url;
        isVizSample = true;
    }

    return { irUrl, vizUrl, isIrSample, isVizSample };
};

export const transformRawAnomalies = (
    rawAnomalies: any,
    reportObservationsAOIs: any,
    anomalyDefinitions: AnomalyDefinitionMap,
    anomalyFileUUIDsSet: Set<string> = new Set(),
    nomenclatureMap?: Map<string, AssetNomenclature>,
): MapAnomalies => {
    const rawMapAnomalies: MapAnomalies = {};

    if (rawAnomalies) {
        const aoiObservations = reportObservationsAOIs ? buildAOIObservations(reportObservationsAOIs) : {};

        // An aoi can effect multiple regions
        rawAnomalies.aois.forEach((aoi: any) => {
            const geometry = aoi?.geometry;

            aoi.aoiAssetRegions.forEach((aoiAssetRegion: any) => {
                const { assetRegionUUID } = aoiAssetRegion;
                // Extract electrical component information
                // Prioritize nomenclature data if available, otherwise use existing data
                const nomenclatureData = nomenclatureMap?.get(assetRegionUUID);
                const combinerId = nomenclatureData?.combinerId ?? aoiAssetRegion.assetRegion?.combinerId;
                const inverterId = nomenclatureData?.inverterId ?? aoiAssetRegion.assetRegion?.inverterId;
                const stackId = nomenclatureData?.stackId ?? aoiAssetRegion.assetRegion?.stack;
                const positionId = nomenclatureData?.positionId ?? aoiAssetRegion.assetRegion?.positionId;

                aoi.anomalyEntries.forEach((anomalyEntry: any) => {
                    const definitionUUID = anomalyEntry.definition_uuid;
                    const definition = anomalyDefinitions[definitionUUID];

                    // Skip if definition not found (can happen with missing anomaly summary data)
                    if (!definition) {
                        return;
                    }

                    const {
                        species,
                        priority,
                        globalDefinition: { nameExternal },
                    } = definition;
                    const { uuid: speciesUuid, irSignal, irModifier, rgbSignal, rgbModifier } = species;
                    const clientAttachmentFilter = buildAnomalyFileStatus(anomalyEntry, anomalyFileUUIDsSet);
                    const statusId = String(anomalyEntry.statusId);
                    const irFilter = buildSignalFilter(irSignal, irModifier);
                    const rgbFilter = buildSignalFilter(rgbSignal, rgbModifier);
                    const observationUUIDs = aoiObservations[aoi.uuid];
                    const anomalyUuid = anomalyEntry.uuid;

                    if (rawMapAnomalies[assetRegionUUID]) {
                        const newAnomaly = {
                            uuid: speciesUuid,
                            name: nameExternal,
                            irFilter,
                            rgbFilter,
                            priority: priority.toString(),
                            statusId,
                            clientAttachmentFilter,
                        };

                        if (rawMapAnomalies[assetRegionUUID]?.species[0].priority > newAnomaly.priority) {
                            rawMapAnomalies[assetRegionUUID].species.unshift(newAnomaly);
                        } else {
                            rawMapAnomalies[assetRegionUUID].species.push(newAnomaly);
                        }

                        // Add observation UUIDs if any
                        if (observationUUIDs) {
                            rawMapAnomalies[assetRegionUUID].observationIds.push(...observationUUIDs);
                        }

                        // Add the anomaly UUID to the anomalyUuids array
                        if (anomalyUuid) {
                            if (!rawMapAnomalies[assetRegionUUID].anomalyUuids) {
                                rawMapAnomalies[assetRegionUUID].anomalyUuids = [anomalyUuid];
                            } else if (!rawMapAnomalies[assetRegionUUID].anomalyUuids.includes(anomalyUuid)) {
                                rawMapAnomalies[assetRegionUUID].anomalyUuids.push(anomalyUuid);
                            }
                        }
                    } else {
                        // Create new entry for this asset region
                        rawMapAnomalies[assetRegionUUID] = {
                            observationIds: observationUUIDs ?? [],
                            position: getCenterPointFromGeometry(geometry),
                            // Add electrical component information
                            combinerId,
                            inverterId,
                            stackId,
                            positionId,
                            anomalyUuids: anomalyUuid ? [anomalyUuid] : [],
                            species: [
                                {
                                    uuid: speciesUuid,
                                    name: nameExternal,
                                    irFilter,
                                    rgbFilter,
                                    priority: priority.toString(),
                                    statusId,
                                    clientAttachmentFilter,
                                },
                            ],
                        };
                    }
                });
            });
        });
    }

    return rawMapAnomalies;
};

export const buildSignalFilter = (signal: string, modifier: string) =>
    signal + (modifier === EMPTY_SIGNAL_OR_MODIFIER ? '' : `-${modifier}`);

// Precision for position-based matching (~1.1 m at equator; longitudinal precision decreases with latitude)
const POSITION_PRECISION = 5;

/**
 * Creates a position-based key for matching anomalies by geographic location.
 * Uses rounded coordinates to handle minor floating-point differences.
 * Note: 5 decimal places provide ~1.1 m precision at the equator. However, longitudinal
 * precision decreases with latitude due to meridian convergence - e.g., at 60° latitude,
 * the same decimal precision represents roughly double the distance on the ground.
 */
const createPositionKey = (position: number[]): string => {
    if (!position || position.length < 2) return '';
    const [lng, lat] = position;
    // Round to approximate meter-level precision for tolerant matching
    // Note: Longitude precision is latitude-dependent; this provides consistent precision for latitude
    const roundedLng = Number(lng.toFixed(POSITION_PRECISION));
    const roundedLat = Number(lat.toFixed(POSITION_PRECISION));
    return `pos:${roundedLng},${roundedLat}`;
};

/**
 * Creates a nomenclature-based key for matching anomalies by module identifiers.
 * Only uses positionId which is module-specific.
 * 
 * Note: Electrical IDs (inverterId, combinerId, stackId) are NOT used because they
 * only provide stack-level precision, not module-level. A stack contains multiple
 * modules, so using electrical IDs would cause key collisions.
 */
const createNomenclatureKey = (anomaly: any): string | null => {
    // Only use positionId - it's the only module-specific identifier
    // Electrical IDs (inverter, combiner, stack) are stack-level, not module-level
    if (anomaly.positionId) {
        return `nom:${anomaly.positionId}`;
    }
    return null;
};

/**
 * Builds fallback lookup maps for anomalies indexed by position and nomenclature.
 * This enables matching across different draft versions where asset_region_uuid differs.
 * 
 * Note: If multiple anomalies share the same position or nomenclature key, only the first
 * encountered anomaly is indexed. Subsequent anomalies with the same key are not stored in
 * these maps and therefore will not be matched via the fallback lookup. In differential modes,
 * such duplicate anomalies may be treated as new or absent, depending on how the comparison is
 * performed. This behavior is intentional to preserve 1:1 matching semantics.
 */
export const buildFallbackLookupMaps = (
    anomalies: any,
): { byPosition: Map<string, any>; byNomenclature: Map<string, any> } => {
    const byPosition = new Map<string, any>();
    const byNomenclature = new Map<string, any>();

    Object.entries(anomalies).forEach(([uuid, anomaly]: [string, any]) => {
        // Index by position - only store first anomaly at each position for 1:1 matching
        const posKey = createPositionKey(anomaly.position);
        if (posKey && !byPosition.has(posKey)) {
            byPosition.set(posKey, { uuid, anomaly });
        }

        // Index by nomenclature - only store first anomaly with each nomenclature key for 1:1 matching
        const nomKey = createNomenclatureKey(anomaly);
        if (nomKey && !byNomenclature.has(nomKey)) {
            byNomenclature.set(nomKey, { uuid, anomaly });
        }
    });

    return { byPosition, byNomenclature };
};

/**
 * Type definition for pre-computed fallback maps used in differential comparison.
 * Build once when anomaly data changes, reuse across all differential mode calculations.
 */
export type FallbackLookupMaps = {
    byPosition: Map<string, any>;
    byNomenclature: Map<string, any>;
};

/**
 * Builds a mapping from previous asset region UUIDs to current asset region UUIDs.
 * This is needed because MVT tiles only contain current inspection's asset region UUIDs.
 * When displaying PREVIOUS or ABSENT anomalies, we need to use the current UUID
 * so the anomalies render correctly on the map tiles.
 */
export const buildPreviousToCurrentUUIDMap = (
    currentAnomalies: any,
    previousAnomalies: any,
    currentFallbackMaps: FallbackLookupMaps,
): Map<string, string> => {
    const previousToCurrentMap = new Map<string, string>();

    Object.entries(previousAnomalies).forEach(([previousUuid, previousAnomaly]: [string, any]) => {
        // Try to find matching current anomaly - returns { uuid, anomaly } directly
        const matched = findMatchingAnomaly(previousUuid, previousAnomaly, currentAnomalies, currentFallbackMaps);
        if (matched) {
            // UUID is now returned directly from findMatchingAnomaly - O(1) instead of O(n)
            previousToCurrentMap.set(previousUuid, matched.uuid);
        }
    });

    return previousToCurrentMap;
};

/**
 * Remaps anomalies keyed by previous UUIDs to use current UUIDs.
 * This allows previous/absent anomalies to render on MVT tiles which only know current UUIDs.
 */
const remapToCurrentUUIDs = (
    anomalies: any,
    previousToCurrentMap: Map<string, string>,
    currentAnomalies: any,
    currentFallbackMaps: FallbackLookupMaps,
): any => {
    const remapped: any = {};

    Object.entries(anomalies).forEach(([previousUuid, anomaly]: [string, any]) => {
        // First check the pre-built map
        let currentUuid = previousToCurrentMap.get(previousUuid);

        // If not in map, try to find a match dynamically (for anomalies not in previous set)
        if (!currentUuid) {
            const matched = findMatchingAnomaly(previousUuid, anomaly, currentAnomalies, currentFallbackMaps);
            if (matched) {
                // UUID is now returned directly from findMatchingAnomaly - O(1) instead of O(n)
                currentUuid = matched.uuid;
            }
        }

        // Use current UUID if found, otherwise keep previous UUID
        // (anomaly won't render on tiles but data is preserved)
        const keyToUse = currentUuid ?? previousUuid;
        remapped[keyToUse] = {
            ...anomaly,
            originalPreviousUuid: previousUuid, // Preserve for reference
        };
    });

    return remapped;
};

/**
 * Result type for findMatchingAnomaly - includes both UUID and anomaly to avoid O(n) reverse lookups
 */
type MatchResult = { uuid: string; anomaly: any } | undefined;

/**
 * Finds a matching anomaly from the lookup source using fallback matching strategies.
 * Tries: 1) Direct UUID lookup, 2) Position matching, 3) Nomenclature matching
 * Returns both the UUID and anomaly to avoid expensive reverse lookups.
 */
const findMatchingAnomaly = (
    uuid: string,
    currentAnomaly: any,
    directLookup: any,
    fallbackMaps: { byPosition: Map<string, any>; byNomenclature: Map<string, any> } | null,
): MatchResult => {
    // Strategy 1: Direct UUID lookup
    if (directLookup[uuid]) {
        return { uuid, anomaly: directLookup[uuid] };
    }

    // If no fallback maps provided, return undefined
    if (!fallbackMaps) {
        return undefined;
    }

    // Strategy 2: Position-based matching
    const posKey = createPositionKey(currentAnomaly.position);
    if (posKey) {
        const posMatch = fallbackMaps.byPosition.get(posKey);
        if (posMatch) {
            return posMatch; // Already has { uuid, anomaly }
        }
    }

    // Strategy 3: Nomenclature-based matching
    const nomKey = createNomenclatureKey(currentAnomaly);
    if (nomKey) {
        const nomMatch = fallbackMaps.byNomenclature.get(nomKey);
        if (nomMatch) {
            return nomMatch; // Already has { uuid, anomaly }
        }
    }

    return undefined;
};

const filterEntriesWithFallback = (
    object: any,
    filterFn: (entry: [string, any], matchedAnomaly: any) => boolean,
    directLookup: any,
    fallbackMaps: { byPosition: Map<string, any>; byNomenclature: Map<string, any> } | null,
    transformFn?: (entry: [string, any], matchedAnomaly: any) => any,
) =>
    Object.entries(object)
        .map(([uuid, anomaly]: [string, any]) => {
            const matchResult = findMatchingAnomaly(uuid, anomaly, directLookup, fallbackMaps);
            // Extract just the anomaly for the filter/transform functions (maintains API compatibility)
            const matchedAnomaly = matchResult?.anomaly;
            return { uuid, anomaly, matchedAnomaly };
        })
        .filter(({ uuid, anomaly, matchedAnomaly }) => filterFn([uuid, anomaly], matchedAnomaly))
        .reduce((acc: any, { uuid, anomaly, matchedAnomaly }) => {
            acc[uuid] = transformFn ? transformFn([uuid, anomaly], matchedAnomaly) : anomaly;
            return acc;
        }, {});

/**
 * Async version of filterEntriesWithFallback that yields periodically to avoid blocking UI
 */
const filterEntriesWithFallbackAsync = async (
    object: any,
    filterFn: (entry: [string, any], matchedAnomaly: any) => boolean,
    directLookup: any,
    fallbackMaps: { byPosition: Map<string, any>; byNomenclature: Map<string, any> } | null,
    transformFn?: (entry: [string, any], matchedAnomaly: any) => any,
): Promise<any> => {
    const entries = Object.entries(object);
    const result: any = {};

    for (let i = 0; i < entries.length; i++) {
        const [uuid, anomaly] = entries[i] as [string, any];
        const matchResult = findMatchingAnomaly(uuid, anomaly, directLookup, fallbackMaps);
        // Extract just the anomaly for the filter/transform functions (maintains API compatibility)
        const matchedAnomaly = matchResult?.anomaly;

        if (filterFn([uuid, anomaly], matchedAnomaly)) {
            result[uuid] = transformFn ? transformFn([uuid, anomaly], matchedAnomaly) : anomaly;
        }

        // Yield every CHUNK_SIZE items
        if ((i + 1) % CHUNK_SIZE === 0) {
            await yieldToMain();
        }
    }

    return result;
};

const filterByPresent = (
    current: any,
    previous: any,
    previousFallbackMaps: { byPosition: Map<string, any>; byNomenclature: Map<string, any> } | null,
) =>
    filterEntriesWithFallback(
        current,
        ([, currentAnomaly]: any, matchedPrevious: any) => haveCommonSpecies(currentAnomaly, matchedPrevious),
        previous,
        previousFallbackMaps,
        ([, currentAnomaly]: any, matchedPrevious: any) => ({
            ...currentAnomaly,
            species:
                currentAnomaly.species.length > matchedPrevious?.species?.length
                    ? filterSpecies(currentAnomaly, matchedPrevious)
                    : currentAnomaly.species,
        }),
    );

const filterByNew = (
    current: any,
    previous: any,
    previousFallbackMaps: { byPosition: Map<string, any>; byNomenclature: Map<string, any> } | null,
) =>
    filterEntriesWithFallback(
        current,
        ([, currentAnomaly]: any, matchedPrevious: any) =>
            !matchedPrevious || !haveSameSpecies(currentAnomaly, matchedPrevious),
        previous,
        previousFallbackMaps,
        ([, currentAnomaly]: any, matchedPrevious: any) => ({
            ...currentAnomaly,
            species: filterSpecies(currentAnomaly, matchedPrevious),
        }),
    );

const filterByAbsent = (
    current: any,
    previous: any,
    currentFallbackMaps: { byPosition: Map<string, any>; byNomenclature: Map<string, any> } | null,
) =>
    filterEntriesWithFallback(
        previous,
        ([, previousAnomaly]: any, matchedCurrent: any) =>
            !matchedCurrent || !haveSameSpecies(matchedCurrent, previousAnomaly),
        current,
        currentFallbackMaps,
        ([, previousAnomaly]: any, matchedCurrent: any) => ({
            ...previousAnomaly,
            species: filterSpecies(previousAnomaly, matchedCurrent),
        }),
    );

/**
 * Pre-computed differential comparison context.
 * Build once when anomaly data changes, reuse across all mode calculations.
 */
export type DifferentialComparisonContext = {
    previousFallbackMaps: FallbackLookupMaps;
    currentFallbackMaps: FallbackLookupMaps;
    previousToCurrentUUIDMap: Map<string, string>;
};

// Chunk size for async processing - process this many items before yielding
const CHUNK_SIZE = 500;

/**
 * Helper to yield control back to the browser during heavy computation
 */
const yieldToMain = (): Promise<void> => new Promise((resolve) => setTimeout(resolve, 0));

/**
 * Async version of buildFallbackLookupMaps that yields periodically to avoid blocking UI
 */
const buildFallbackLookupMapsAsync = async (
    anomalies: any,
): Promise<FallbackLookupMaps> => {
    const byPosition = new Map<string, any>();
    const byNomenclature = new Map<string, any>();
    const entries = Object.entries(anomalies);

    for (let i = 0; i < entries.length; i++) {
        const [uuid, anomaly] = entries[i] as [string, any];

        // Index by position
        const posKey = createPositionKey(anomaly.position);
        if (posKey && !byPosition.has(posKey)) {
            byPosition.set(posKey, { uuid, anomaly });
        }

        // Index by nomenclature
        const nomKey = createNomenclatureKey(anomaly);
        if (nomKey && !byNomenclature.has(nomKey)) {
            byNomenclature.set(nomKey, { uuid, anomaly });
        }

        // Yield every CHUNK_SIZE items
        if ((i + 1) % CHUNK_SIZE === 0) {
            await yieldToMain();
        }
    }

    return { byPosition, byNomenclature };
};

/**
 * Async version of buildPreviousToCurrentUUIDMap that yields periodically
 */
const buildPreviousToCurrentUUIDMapAsync = async (
    currentAnomalies: any,
    previousAnomalies: any,
    currentFallbackMaps: FallbackLookupMaps,
): Promise<Map<string, string>> => {
    const previousToCurrentMap = new Map<string, string>();
    const entries = Object.entries(previousAnomalies);

    for (let i = 0; i < entries.length; i++) {
        const [previousUuid, previousAnomaly] = entries[i] as [string, any];

        // findMatchingAnomaly now returns { uuid, anomaly } directly - O(1) instead of O(n)
        const matched = findMatchingAnomaly(previousUuid, previousAnomaly, currentAnomalies, currentFallbackMaps);
        if (matched) {
            previousToCurrentMap.set(previousUuid, matched.uuid);
        }

        // Yield every CHUNK_SIZE items
        if ((i + 1) % CHUNK_SIZE === 0) {
            await yieldToMain();
        }
    }

    return previousToCurrentMap;
};

/**
 * Async version that builds the differential comparison context without blocking UI.
 * This should be used for large datasets.
 */
export const buildDifferentialComparisonContextAsync = async (
    currentMapAnomalies: any,
    previousMapAnomalies: any,
): Promise<DifferentialComparisonContext> => {
    const previousFallbackMaps = await buildFallbackLookupMapsAsync(previousMapAnomalies);
    await yieldToMain();

    const currentFallbackMaps = await buildFallbackLookupMapsAsync(currentMapAnomalies);
    await yieldToMain();

    const previousToCurrentUUIDMap = await buildPreviousToCurrentUUIDMapAsync(
        currentMapAnomalies,
        previousMapAnomalies,
        currentFallbackMaps,
    );

    return {
        previousFallbackMaps,
        currentFallbackMaps,
        previousToCurrentUUIDMap,
    };
};

/**
 * Builds the differential comparison context once.
 * This should be called when current or previous anomaly data changes,
 * not on every mode switch.
 */
export const buildDifferentialComparisonContext = (
    currentMapAnomalies: any,
    previousMapAnomalies: any,
): DifferentialComparisonContext => {
    const previousFallbackMaps = buildFallbackLookupMaps(previousMapAnomalies);
    const currentFallbackMaps = buildFallbackLookupMaps(currentMapAnomalies);
    const previousToCurrentUUIDMap = buildPreviousToCurrentUUIDMap(
        currentMapAnomalies,
        previousMapAnomalies,
        currentFallbackMaps,
    );

    return {
        previousFallbackMaps,
        currentFallbackMaps,
        previousToCurrentUUIDMap,
    };
};

export const filterDifferentialAnomalies = (
    currentMapAnomalies: any,
    previousMapAnomalies: any,
    mode: DIFFERENTIAL_MODES,
    context?: DifferentialComparisonContext,
): any => {
    // Use provided context or build it (for backwards compatibility)
    const {
        previousFallbackMaps,
        currentFallbackMaps,
        previousToCurrentUUIDMap,
    } = context ?? buildDifferentialComparisonContext(currentMapAnomalies, previousMapAnomalies);

    switch (mode) {
        case DIFFERENTIAL_MODES.PRESENT:
            return filterByPresent(currentMapAnomalies, previousMapAnomalies, previousFallbackMaps);

        case DIFFERENTIAL_MODES.CHANGED_CURRENT:
            return filterEntriesWithFallback(
                currentMapAnomalies,
                ([, currentAnomaly]: any, matchedPrevious: any) =>
                    matchedPrevious && !haveSameSpecies(currentAnomaly, matchedPrevious),
                previousMapAnomalies,
                previousFallbackMaps,
            );
        case DIFFERENTIAL_MODES.CHANGED_PREVIOUS: {
            // Filter previous anomalies, then remap to current UUIDs for MVT tile rendering
            const filtered = filterEntriesWithFallback(
                previousMapAnomalies,
                ([, previousAnomaly]: any, matchedCurrent: any) =>
                    matchedCurrent && !haveSameSpecies(matchedCurrent, previousAnomaly),
                currentMapAnomalies,
                currentFallbackMaps,
            );
            return remapToCurrentUUIDs(filtered, previousToCurrentUUIDMap, currentMapAnomalies, currentFallbackMaps);
        }
        case DIFFERENTIAL_MODES.ABSENT: {
            // Filter absent anomalies, then remap to current UUIDs for MVT tile rendering
            const filtered = filterByAbsent(currentMapAnomalies, previousMapAnomalies, currentFallbackMaps);
            return remapToCurrentUUIDs(filtered, previousToCurrentUUIDMap, currentMapAnomalies, currentFallbackMaps);
        }

        case DIFFERENTIAL_MODES.NEW:
            return filterByNew(currentMapAnomalies, previousMapAnomalies, previousFallbackMaps);

        case DIFFERENTIAL_MODES.LATEST:
            return currentMapAnomalies;

        case DIFFERENTIAL_MODES.PREVIOUS:
            // Remap all previous anomalies to current UUIDs for MVT tile rendering
            return remapToCurrentUUIDs(
                previousMapAnomalies,
                previousToCurrentUUIDMap,
                currentMapAnomalies,
                currentFallbackMaps,
            );

        default:
            return currentMapAnomalies;
    }
};

/**
 * Async version of filterDifferentialAnomalies that yields periodically to avoid blocking UI.
 * Should be used for large datasets.
 */
export const filterDifferentialAnomaliesAsync = async (
    currentMapAnomalies: any,
    previousMapAnomalies: any,
    mode: DIFFERENTIAL_MODES,
    context: DifferentialComparisonContext,
): Promise<any> => {
    const { previousFallbackMaps, currentFallbackMaps, previousToCurrentUUIDMap } = context;

    switch (mode) {
        case DIFFERENTIAL_MODES.PRESENT:
            return filterEntriesWithFallbackAsync(
                currentMapAnomalies,
                ([, currentAnomaly]: any, matchedPrevious: any) => haveCommonSpecies(currentAnomaly, matchedPrevious),
                previousMapAnomalies,
                previousFallbackMaps,
                ([, currentAnomaly]: any, matchedPrevious: any) => ({
                    ...currentAnomaly,
                    species:
                        currentAnomaly.species.length > matchedPrevious?.species?.length
                            ? filterSpecies(currentAnomaly, matchedPrevious)
                            : currentAnomaly.species,
                }),
            );

        case DIFFERENTIAL_MODES.CHANGED_CURRENT:
            return filterEntriesWithFallbackAsync(
                currentMapAnomalies,
                ([, currentAnomaly]: any, matchedPrevious: any) =>
                    matchedPrevious && !haveSameSpecies(currentAnomaly, matchedPrevious),
                previousMapAnomalies,
                previousFallbackMaps,
            );

        case DIFFERENTIAL_MODES.CHANGED_PREVIOUS: {
            const filtered = await filterEntriesWithFallbackAsync(
                previousMapAnomalies,
                ([, previousAnomaly]: any, matchedCurrent: any) =>
                    matchedCurrent && !haveSameSpecies(matchedCurrent, previousAnomaly),
                currentMapAnomalies,
                currentFallbackMaps,
            );
            return remapToCurrentUUIDs(filtered, previousToCurrentUUIDMap, currentMapAnomalies, currentFallbackMaps);
        }

        case DIFFERENTIAL_MODES.ABSENT: {
            const filtered = await filterEntriesWithFallbackAsync(
                previousMapAnomalies,
                ([, previousAnomaly]: any, matchedCurrent: any) =>
                    !matchedCurrent || !haveSameSpecies(matchedCurrent, previousAnomaly),
                currentMapAnomalies,
                currentFallbackMaps,
                ([, previousAnomaly]: any, matchedCurrent: any) => ({
                    ...previousAnomaly,
                    species: filterSpecies(previousAnomaly, matchedCurrent),
                }),
            );
            return remapToCurrentUUIDs(filtered, previousToCurrentUUIDMap, currentMapAnomalies, currentFallbackMaps);
        }

        case DIFFERENTIAL_MODES.NEW:
            return filterEntriesWithFallbackAsync(
                currentMapAnomalies,
                ([, currentAnomaly]: any, matchedPrevious: any) =>
                    !matchedPrevious || !haveSameSpecies(currentAnomaly, matchedPrevious),
                previousMapAnomalies,
                previousFallbackMaps,
                ([, currentAnomaly]: any, matchedPrevious: any) => ({
                    ...currentAnomaly,
                    species: filterSpecies(currentAnomaly, matchedPrevious),
                }),
            );

        case DIFFERENTIAL_MODES.LATEST:
            return currentMapAnomalies;

        case DIFFERENTIAL_MODES.PREVIOUS:
            return remapToCurrentUUIDs(
                previousMapAnomalies,
                previousToCurrentUUIDMap,
                currentMapAnomalies,
                currentFallbackMaps,
            );

        default:
            return currentMapAnomalies;
    }
};

/**
 * Checks if two anomalies share at least one common species.
 * Uses Set for O(s1 + s2) complexity instead of O(s1 × s2) nested loops.
 */
const haveCommonSpecies = (anomaly1: any, anomaly2: any): boolean => {
    if (!anomaly1?.species?.length || !anomaly2?.species?.length) return false;
    // Build Set once O(s2), then O(1) lookups for each species in anomaly1
    const species2Set = new Set(anomaly2.species.map((s: any) => s.uuid));
    return anomaly1.species.some(({ uuid }: any) => species2Set.has(uuid));
};

/**
 * Checks if two anomalies have exactly the same species.
 * Uses Set for O(s1 + s2) complexity instead of O(s1 × s2) nested loops.
 */
const haveSameSpecies = (anomaly1: any, anomaly2: any): boolean => {
    if (anomaly1?.species?.length !== anomaly2?.species?.length) return false;
    if (!anomaly1?.species?.length) return true; // Both empty
    // Build Set once O(s2), then O(1) lookups for each species in anomaly1
    const species2Set = new Set(anomaly2.species.map((s: any) => s.uuid));
    return anomaly1.species.every(({ uuid }: any) => species2Set.has(uuid));
};

/**
 * Returns species from anomaly1 that are not present in anomaly2.
 * Uses Set for O(s1 + s2) complexity instead of O(s1 × s2) nested loops.
 */
const filterSpecies = (anomaly1: any, anomaly2: any): any[] => {
    if (!anomaly2?.species?.length) return anomaly1.species;
    // Build Set once O(s2), then O(1) lookups for each species in anomaly1
    const species2Set = new Set(anomaly2.species.map((s: any) => s.uuid));
    return anomaly1.species.filter(({ uuid }: any) => !species2Set.has(uuid));
};

// ============================================================================
// UUID-ONLY FAST PATH FUNCTIONS
// These functions only use direct UUID matching (no position/nomenclature fallback)
// Used for the initial fast pass before determining if fallback is needed
// ============================================================================

/**
 * Fast UUID-only filter for PRESENT mode.
 * Returns anomalies present in both current and previous (by UUID match).
 */
const filterByPresentUUIDOnly = (current: any, previous: any): any =>
    Object.fromEntries(
        Object.entries(current).filter(([uuid, currentAnomaly]: [string, any]) =>
            haveCommonSpecies(currentAnomaly, previous[uuid]),
        ),
    );

/**
 * Fast UUID-only filter for CHANGED_CURRENT mode.
 * Returns current anomalies that exist in previous but have different species.
 */
const filterByChangedCurrentUUIDOnly = (current: any, previous: any): any =>
    Object.fromEntries(
        Object.entries(current).filter(([uuid, currentAnomaly]: [string, any]) =>
            previous[uuid] && !haveSameSpecies(currentAnomaly, previous[uuid]),
        ),
    );

/**
 * Fast UUID-only filter for NEW mode.
 * Returns anomalies in current that don't exist or have different species in previous.
 */
const filterByNewUUIDOnly = (current: any, previous: any): any =>
    Object.fromEntries(
        Object.entries(current)
            .filter(([uuid, currentAnomaly]: [string, any]) =>
                !previous[uuid] || !haveSameSpecies(currentAnomaly, previous[uuid]),
            )
            .map(([uuid, currentAnomaly]: [string, any]) => [
                uuid,
                {
                    ...currentAnomaly,
                    species: filterSpecies(currentAnomaly, previous[uuid]),
                },
            ]),
    );

/**
 * Fast UUID-only filter for ABSENT mode.
 * Returns anomalies in previous that don't exist or have different species in current.
 */
const filterByAbsentUUIDOnly = (current: any, previous: any): any =>
    Object.fromEntries(
        Object.entries(previous)
            .filter(([uuid, previousAnomaly]: [string, any]) =>
                !current[uuid] || !haveSameSpecies(current[uuid], previousAnomaly),
            )
            .map(([uuid, previousAnomaly]: [string, any]) => [
                uuid,
                {
                    ...previousAnomaly,
                    species: filterSpecies(previousAnomaly, current[uuid]),
                },
            ]),
    );

/**
 * Fast UUID-only differential filtering (no fallback).
 * Used as the first pass to quickly determine if fallback is needed.
 */
export const filterDifferentialAnomaliesUUIDOnly = (
    currentMapAnomalies: any,
    previousMapAnomalies: any,
    mode: DIFFERENTIAL_MODES,
): any => {
    switch (mode) {
        case DIFFERENTIAL_MODES.PRESENT:
            return filterByPresentUUIDOnly(currentMapAnomalies, previousMapAnomalies);

        case DIFFERENTIAL_MODES.CHANGED_CURRENT:
            return filterByChangedCurrentUUIDOnly(currentMapAnomalies, previousMapAnomalies);

        case DIFFERENTIAL_MODES.CHANGED_PREVIOUS:
            // For CHANGED_PREVIOUS, we need to look at previous anomalies
            return Object.fromEntries(
                Object.entries(previousMapAnomalies).filter(
                    ([uuid, previousAnomaly]: [string, any]) =>
                        currentMapAnomalies[uuid] && !haveSameSpecies(currentMapAnomalies[uuid], previousAnomaly),
                ),
            );

        case DIFFERENTIAL_MODES.ABSENT:
            return filterByAbsentUUIDOnly(currentMapAnomalies, previousMapAnomalies);

        case DIFFERENTIAL_MODES.NEW:
            return filterByNewUUIDOnly(currentMapAnomalies, previousMapAnomalies);

        case DIFFERENTIAL_MODES.LATEST:
            return currentMapAnomalies;

        case DIFFERENTIAL_MODES.PREVIOUS:
            return previousMapAnomalies;

        default:
            return currentMapAnomalies;
    }
};

/**
 * Checks if fallback matching is needed by examining UUID-only matching results.
 * Returns true if no UUID matches are found, indicating potential draft version
 * mismatch where UUIDs differ but anomalies should match.
 * 
 * Uses early exit - returns false as soon as ANY UUID match is found (O(1) best case).
 * Only returns true (fallback needed) after checking ALL anomalies to avoid false positives.
 */
export const checkIfFallbackNeeded = (
    currentMapAnomalies: any,
    previousMapAnomalies: any,
): boolean => {
    const currentKeys = Object.keys(currentMapAnomalies);
    const previousCount = Object.keys(previousMapAnomalies).length;
    
    // Quick check: if either dataset is empty, no fallback needed
    if (currentKeys.length === 0 || previousCount === 0) {
        return false;
    }

    // Early exit: if ANY UUID exists in both datasets, fallback is not needed
    // Uses for loop for early exit on first match (O(1) best case)
    // Must check ALL before returning true to avoid false positives
    for (let i = 0; i < currentKeys.length; i++) {
        if (previousMapAnomalies[currentKeys[i]] !== undefined) {
            // Found a UUID match - draft versions are compatible, no fallback needed
            return false;
        }
    }

    // No UUID matches found after checking all - fallback is needed
    return true;
};

// ============================================================================
// END UUID-ONLY FAST PATH FUNCTIONS
// ============================================================================

const buildAnomalyFileStatus = (anomalyEntry: any, anomalyFileUUIDsSet: Set<string>): string => {
    const hasClientAttachment = anomalyFileUUIDsSet.has(anomalyEntry.uuid);

    return hasClientAttachment ? 'Client Attachment' : 'No Attachment';
};

export const sortObjectKeysByCount = (object: any) =>
    Object.entries(object)
        .sort(([, a]: any, [, b]: any) => b.count - a.count)
        .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});

export const getAssetLabels = (assetData: any, assetType: MAP_LAYER_ASSET_TYPES) => {
    const assetRegions =
        assetData?.reportTask?.superReportTaskSuperAnalysisTask?.[0]?.superAnalysisTask?.superTask?.draftVersion
            ?.assetRegions;

    if (assetRegions) {
        const labels = assetRegions
            .map((data: any) => {
                const label =
                    data[AssetTypes[assetType].nom] ?? data.nomProperties[AssetTypes[assetType].backupNomProperty];

                return {
                    uuid: data.uuid,
                    position: getCenterPointFromGeometry(data.geometry),
                    label,
                };
            })
            .filter((data: any) => data.label);

        return { labels, disabled: !labels.length };
    }
};

export const createAnomalyDefinitionMap = (anomalySummary: any): AnomalyDefinitionMap =>
    anomalySummary?.reduce((definitionMap: AnomalyDefinitionMap, summary: any) => {
        definitionMap[summary.definition.uuid] = summary.definition;

        return definitionMap;
    }, {}) ?? {};
